import React, { useEffect, useState } from 'react';

import { Alert, Modal, Form, Input } from 'antd';
import DeviceTypeRadioSelector, { DeviceTypeRadioValue } from '~components/DeviceTypeRadioSelector';
import { useHasModalOpened } from '../../../hooks/useHasModalOpened';
import { isValidUrl } from '../../validators/url';
import { useEvent } from '../../events/hooks/useEvent';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import { useAddURLsMutation } from '../CacheManagerApi';
import { AdaptiveType } from './CachedPage';

const radioValueToAdaptiveTypes = (value: DeviceTypeRadioValue): AdaptiveType[] =>
  value === 'all' ? ['desktop', 'mobile'] : [value];

const GENERIC_ERROR = 'An error occurred while adding URLs. Please try again later.';

type CachedPagesAddProps = {
  onSuccess: (addedElements: number) => void;
  mobileAdaptive: boolean;
  showModal: boolean;
  onModalClose: () => void;
};

export default function CachedPagesAdd({ onSuccess, mobileAdaptive, showModal, onModalClose }: CachedPagesAddProps) {
  const defaultAdaptiveTypes: AdaptiveType[] = mobileAdaptive ? ['desktop', 'mobile'] : ['desktop'];
  const [adaptiveTypes, setAdaptiveTypes] = useState(defaultAdaptiveTypes);
  const [customMessage, setCustomMessage] = useState('');
  const [urlsAlreadyExists, setUrlsAlreadyExists] = useState<{ url: string; adaptiveType: AdaptiveType }[]>([]);
  const [addUrls, addUrlsResult] = useAddURLsMutation();
  const { track } = useEvent();
  const prerenderUser = usePrerenderUser();
  const [form] = Form.useForm();
  const hasModalOpened = useHasModalOpened(showModal);

  useEffect(() => {
    if (hasModalOpened) {
      form.setFieldsValue({ urls: '' });
    }
    setUrlsAlreadyExists([]);
  }, [showModal, hasModalOpened]);

  const handleModalClose = () => {
    setUrlsAlreadyExists([]);
    setCustomMessage('');
    addUrlsResult.reset();
    onModalClose();
  };

  const saveUrlList = async (urlList: string[], adaptiveTypes: AdaptiveType[]) => {
    const urls = urlList.flatMap((url) =>
      adaptiveTypes.map((adaptiveType) => ({
        url,
        adaptiveType,
      }))
    );
    try {
      const addUrlsResult = (await addUrls(urls).unwrap()).data.result;
      const urlsAlreadyExistsInAddedResult = addUrlsResult.filter((r) => r.error?.urlAlreadyExists).map((e) => e.item);
      setUrlsAlreadyExists(urlsAlreadyExistsInAddedResult);
      const addedElements = addUrlsResult.filter((r) => !r.error?.message);
      if (addedElements.length > 0) {
        track('Cache Manager URL Added', {
          subscription_plan: prerenderUser.chargebeePlanId,
        });
      }
      if (urlsAlreadyExistsInAddedResult.length === 0) {
        onSuccess(addedElements.length);
        handleModalClose();
      }
    } catch {
        // Ignore add URLs errors
      }
  };

  const saveForm = ({ urls }: { urls: string }) => {
    if (!urls) return;

    const urlList = urls.split('\n').filter((u) => !!u);
    const invalidLine = urlList.findIndex((u) => !isValidUrl(u));

    // Set the error message
    setCustomMessage(
      urlList.length > 100
        ? `You list of ${urlList.length} URLs exceeds 100 URLs.`
        : invalidLine !== -1
        ? `Your list contains an invalid URL in line ${invalidLine + 1}.`
        : ''
    );

    if (urlList.length > 0 && invalidLine === -1) {
      saveUrlList(urlList, adaptiveTypes);
    }
  };

  const renderUrlsAlreadyExistsBox = () => {
    const MAX_URLS_TO_SHOW = 10;
    const urlsAlreadyExistsHead = urlsAlreadyExists.slice(0, MAX_URLS_TO_SHOW);
    return (
      <Alert
        type="error"
        message="URLs already exist!"
        description={
          <>
            {urlsAlreadyExistsHead.map((u) => (
              <div key={`url-already-exists-${u.url}-${u.adaptiveType}`} className="text-truncate">
                {u.url} - {u.adaptiveType}
              </div>
            ))}
            {urlsAlreadyExists.length > MAX_URLS_TO_SHOW && <div>...</div>}
          </>
        }
      />
    );
  };

  return (
    <>
      <Modal
        closable
        open={showModal}
        title="Add URLs"
        okText="Add"
        cancelText="Cancel"
        onCancel={handleModalClose}
        okButtonProps={{ htmlType: 'submit', form: 'addCachedPage', loading: addUrlsResult.isLoading }}
        styles={{ footer: { display: 'flex', justifyContent: 'space-between' } }}
      >
        {addUrlsResult.isError && <Alert showIcon type="error" message={GENERIC_ERROR} />}

        {customMessage && <Alert showIcon type="warning" message={customMessage} />}

        {urlsAlreadyExists.length ? <div style={{ marginTop: 8 }}>{renderUrlsAlreadyExistsBox()}</div> : null}

        <Form id="addCachedPage" form={form} layout="vertical" onFinish={saveForm}>
          <p style={{ marginTop: 16, marginBottom: 16 }}>Enter a list of up to 100 URLs</p>
          <p style={{ marginBottom: 4 }}>Device type</p>

          <DeviceTypeRadioSelector
            mobileOptimizedEnabled={mobileAdaptive}
            defaultValue={mobileAdaptive ? 'all' : 'desktop'}
            onChange={(value) => setAdaptiveTypes(radioValueToAdaptiveTypes(value))}
          />

          <Form.Item name="urls">
            <Input.TextArea
              rows={10}
              placeholder="https://example.com/index.html&#10;https://example.com/about&#10;..."
              style={{ marginTop: 16 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
